import React, { Component } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import FormikControl from '../components/FormikContainer/FormikControl';
import { useHistory } from 'react-router-dom';
import { Segment, VerticalSpacer, dismissForever, dismissTemporarily, isShouldShow } from '../core/core';
import {
  getRecommendedReadingLevelFromGrade,
  getUsersEnableContentTags,
  isUserHasKey,
  noWhiteSpace,
} from '../GlobalFunctions';
import { getStore, AppDispatch, RootState } from '../ApplicationState';
import { ClassroomCard } from './ClassroomCard';
import { logger } from '../error-tracker';
import { lockWithPreviewUnlessHasAny } from '../lockDecorators';
import { HintIcon } from '../HintIcon';
import {
  State as ClassroomsState,
  createClassroom,
  deleteClassroom,
  replaceClassroom,
  selectClassroom,
  updateClassroom,
} from '../Classrooms';
import {
  getSignedInGoogleUserId,
  initializeGoogleApiClients,
  isGoogleUserSignedIn,
  promptUserGoogleSignIn,
} from '../UserManagementHelpers';
import { FieldUpdatePair, LoginState, updateField } from '../UserManagement';
import {
  getAuthorizationToken,
  getClassroom,
  removeStudentAccount,
  updateClassroom as updateClassroomBackend,
  createClassroom as createClassroomBackend,
  removeClassroom,
} from '../BackendInterface';
import { useErrorStatus } from '../components/EnableErrorMessagePopup/EnableErrorMessagePopup';
import SpinnerOverlay from '../SpinnerOverlay';
import ParentalConsentWarning from '../components/ParentalConsentWarning/ParentalConsentWarning';
import ParentalConsentWarningModal from '../components/ParentalConsentWarningModal/ParentalConsentWarningModal';
import ImportClassroomModal from './ImportClassroomModal';
import DeleteClassroomModal from './DeleteClassroomModal';
import ArchiveClassroomModal from './ArchiveClassroomModal';
import DetailsButton from './DetailsButton';
import MarketingClassroom from './MarketingClassroom';
import PrimaryButton from '../components/PrimaryButton/PrimaryButton';
import SecondaryButton from '../components/SecondaryButton/SecondaryButton';
import TextButton from '../components/TextButton/TextButton';
import { Dialog, DialogContent } from '../components/Dialog';
import googleClassroomLogo from '../images/32x32_yellow_stroke_icon@1x.png';
import cleverClassroomLogo from '../images/CleverClassroom-Icon.png';
import classLinkClassroomLogo from '../images/ClassLinkClassroom-Icon.png';
import cx from 'classnames';
import utilityStyles from '../SCSS/common/utilityStyles.module.scss';
import segmentStyles from '../SCSS/Segment.module.scss';
import styles from '../SCSS/ClassroomCard.module.scss';
import { ImportSource } from '../CleverRostering/CleverRosteringTypes';
import { History, Location } from 'history';
import { ClassroomType, CreateNewClassroomType } from '@peekapak/types/DataProtocolTypes';
import { cloneDeep } from 'lodash';

type ClassroomProps = ClassroomPropsFromRedux & {
  loginState: LoginState;
  history: History;
  location: Location;
  isAuthenticated: boolean;
  onShowGetStartedDialog: () => void;
  params: Record<string, string>;
  onShowErrorMessage: (message: string) => void;
  isUnAuthenticated?: boolean;
};
interface ClassroomState {
  isShowDetailsModal: boolean;
  isShowImportModal: boolean;
  isShowDeleteModal: boolean;
  isShowArchiveModal: boolean;
  isShowParentalConsentWarningModal: boolean;
  isEditingClassroom: boolean;
  isAnimateOnCreation: boolean;
  importSource: ImportSource | undefined;
  classroomToAnimate: string | undefined;
  classroomUnderEditIndex: number | undefined;
  classroomUnderEdit: ClassroomType | undefined;
  classroomUnderDelete: string | undefined;
  classroomUnderDeleteNumOfStudents: number | undefined;
  isShowSpinner?: boolean;
}
class Classroom extends Component<ClassroomProps, ClassroomState> {
  parentalConsentWarningModalAcknowledgeFollowUpFunction: () => Promise<void>;
  constructor(props: ClassroomProps) {
    super(props);

    this.state = {
      isShowDetailsModal: false,
      isShowImportModal: false,
      isShowDeleteModal: false,
      isShowArchiveModal: false,
      isShowParentalConsentWarningModal: false,
      isEditingClassroom: false,
      isAnimateOnCreation: false,
      importSource: undefined,
      classroomToAnimate: undefined,
      classroomUnderEditIndex: undefined,
      classroomUnderEdit: undefined,
      classroomUnderDelete: undefined,
      classroomUnderDeleteNumOfStudents: undefined,
    };

    this.closeClassroomDetailsModal = this.closeClassroomDetailsModal.bind(this);
    this.openClassroomDetailsModal = this.openClassroomDetailsModal.bind(this);
    this.handleNewClassroom = this.handleNewClassroom.bind(this);
    this.handleCreateClassroom = this.handleCreateClassroom.bind(this);
    this.handleSaveChanges = this.handleSaveChanges.bind(this);
    this.handleDeleteClassroomNoStudent = this.handleDeleteClassroomNoStudent.bind(this);
    this.handleDeleteClassroomWithStudent = this.handleDeleteClassroomWithStudent.bind(this);
    this.handleOpenArchiveClassroomModal = this.handleOpenArchiveClassroomModal.bind(this);
    this.handleToggleArchive = this.handleToggleArchive.bind(this);
    this.handleOnSettings = this.handleOnSettings.bind(this);
    this.isClassroomNameValid = this.isClassroomNameValid.bind(this);
    this.turnOffCreationAnimation = this.turnOffCreationAnimation.bind(this);
  }

  onHideParentalConsentWarningModal = () => this.setState({ isShowParentalConsentWarningModal: false });

  onAcknowledgeParentalConsentWarning = (dismissForeverFlag: boolean) => {
    if (dismissForeverFlag) {
      dismissForever('ParentalConsentWarning', this.props.userProfile?.flags);
    } else {
      dismissTemporarily('ParentalConsentWarning');
    }

    this.onHideParentalConsentWarningModal();
    this.parentalConsentWarningModalAcknowledgeFollowUpFunction();
  };

  handleImportNewClassroom = () => {
    if (isShouldShow(this.props.userProfile, 'ParentalConsentWarning')) {
      this.parentalConsentWarningModalAcknowledgeFollowUpFunction = this.handleImportNewClassroomInner;
      this.setState({
        isShowParentalConsentWarningModal: true,
      });
    } else {
      this.handleImportNewClassroomInner();
    }
  };

  handleImportNewClassroomInner = async () => {
    const userSignedIn = isGoogleUserSignedIn();
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    //
    // logic
    // if Google api says user is not signed in, then sign in the user
    // if Google api says user is signed in, then we need to check a few things
    //    - do we have an Cognito-associated Google Id? if we do, we need to
    //    check that it matches the current Id in the Google api
    //    - do we already know the Google Id? if so, then we don't need to do
    //    anything else and we can open up the import dialog
    //    - the order of these two check is important, because we may already
    //    know the Google Id, it may nevertheless not match with the current
    //    logged in user's Cognito Google Id. The assumption here is that if a
    //    Cognito user also logs in with a Google account, we want to get
    //    classrooms from that account, not give the ability to open up someone
    //    else's Google classrooms
    //

    try {
      if (!userSignedIn) {
        logger.logMessage(`Classroom page: detected Google user not signed in, attempting to sign in now`);
        await signInGoogleUser();
      } else {
        const cognitoGoogleId = getGoogleIdIfExists();
        await signInGoogleUserIfRequired(cognitoGoogleId);
      }

      this.setState({
        importSource: ImportSource.GOOGLE,
        isShowImportModal: true,
      });
    } catch (error) {
      parseError(error as ErrorEvent);
    }

    async function signInGoogleUserIfRequired(cognitoGoogleId: string) {
      if (cognitoGoogleId) {
        if (isGoogleIdMismatch(cognitoGoogleId)) {
          logger.logMessage(
            `Classroom page: detected Google user signed in but does not match Cognito user, attempting to sign in again`,
          );
          await signInGoogleUser();
        }
      } else if (isSignedInButIdNotSaved()) {
        logger.logMessage(`Classroom page: detected Google account not connected, attempting to sign in now`);
        await signInGoogleUser();
      }
    }

    function isSignedInButIdNotSaved() {
      return userSignedIn && !that.props.userProfile?.googleAccountUserId;
    }

    function isGoogleIdMismatch(cognitoGoogleId: string) {
      const currentUserId = getSignedInGoogleUserId();

      if (currentUserId !== cognitoGoogleId) {
        logger.logMessage(
          `Classroom page: detected Google user signed in but does not match Cognito user, attempting to sign in again`,
        );
        return true;
      }

      return false;
    }

    function getGoogleIdIfExists() {
      const stateUser = getStore().getState().user;
      const identities = stateUser.cognitoProfile?.signInUserSession.idToken.payload.identities;

      if (identities) {
        const googleIdentity = identities.filter((identity) => {
          return identity.providerName === 'Google';
        });

        if (googleIdentity.length > 0) {
          return googleIdentity[0].userId;
        }

        return null;
      }
    }

    function parseError(error: ErrorEvent) {
      if (error.error === 'popup_closed_by_user') {
        that.props.onShowErrorMessage(
          'You must sign in with a Google Classroom-connected account to import a Google classroom',
        );
      } else if (error.error === 'access_denied') {
        that.props.onShowErrorMessage(
          'You must grant Peekapak permission to access your Google classroom data in order to import from that classroom',
        );
      } else {
        throw error;
      }
    }

    async function signInGoogleUser() {
      await promptUserGoogleSignIn();
      if (isGoogleUserSignedIn()) {
        that.props.updateUserProfile({
          field: 'googleAccountUserId',
          value: getSignedInGoogleUserId(),
        });
      }
    }
  };

  handleImportNewCleverClassroom = () => {
    if (isShouldShow(this.props.userProfile, 'ParentalConsentWarning')) {
      this.parentalConsentWarningModalAcknowledgeFollowUpFunction = this.handleImportNewCleverClassroomInner;
      this.setState({
        isShowParentalConsentWarningModal: true,
      });
    } else {
      this.handleImportNewCleverClassroomInner();
    }
  };

  handleImportNewCleverClassroomInner = async () => {
    this.props.history.push('/classroom/rostering/Clever/classroomSettings');
  };

  handleImportNewClassLinkClassroom = () => {
    if (isShouldShow(this.props.userProfile, 'ParentalConsentWarning')) {
      this.parentalConsentWarningModalAcknowledgeFollowUpFunction = this.handleImportNewClassLinkClassroomInner;
      this.setState({
        isShowParentalConsentWarningModal: true,
      });
    } else {
      this.handleImportNewClassLinkClassroomInner();
    }
  };

  handleImportNewClassLinkClassroomInner = async () => {
    this.props.history.push('/classroom/rostering/ClassLink/classroomSettings');
  };
  closeImportModal = () => {
    this.setState({
      importSource: ImportSource.UNDEFINED,
      isShowImportModal: false,
    });
  };

  isClassroomNameValid(nameToCompare: string) {
    const existingClassrooms = this.props.classrooms.list;

    if (!nameToCompare || !(nameToCompare.trim().length > 0) || nameToCompare.toLowerCase() === 'default') {
      return false;
    }

    const name = nameToCompare.trim();

    // if creating new classroom, then finding
    // the same name in array means name is invalid
    if (!this.state.isEditingClassroom) {
      const found = existingClassrooms.find((classroom) => classroom.className === name);

      if (!found) {
        return true;
      }

      return false;
    }

    // if editing classroom details then
    // name is valid if it's not found in array
    // or if it's found, but it's the same
    // name as the classroom being edited
    const foundIndex = existingClassrooms.findIndex((classroom) => classroom.className === name);

    return foundIndex < 0 || foundIndex === this.state.classroomUnderEditIndex;
  }

  closeClassroomDetailsModal() {
    this.setState({
      isShowDetailsModal: false,
      isEditingClassroom: false,
      classroomUnderEditIndex: undefined,
      classroomUnderEdit: undefined,
    });
  }

  openClassroomDetailsModal() {
    this.setState({ isShowDetailsModal: true });
  }

  handleNewClassroom(event: React.MouseEvent) {
    event.preventDefault();

    const newClassroom = {} as ClassroomType;

    this.setState({
      isShowDetailsModal: true,
      isEditingClassroom: false,
      classroomUnderEdit: newClassroom,
    });
  }

  onImportDoneCallback = async (newClassroomObject: ClassroomType | null) => {
    this.setState({ isShowSpinner: false });
    this.closeImportModal();

    if (newClassroomObject) {
      this.props.createClassroom(newClassroomObject);
      this.setState({ isShowSpinner: true });
      try {
        const result = await getClassroom(newClassroomObject.classroomId);
        this.setState({ isShowSpinner: false });
        this.props.updateClassroom(result);
        this.setState({
          isAnimateOnCreation: true,
          classroomToAnimate: newClassroomObject.className,
        });
      } catch (_) {
        this.setState({ isShowSpinner: false });
        console.error('Student::handleSaveChanges - postServiceRequest update failed');
      }
    }
  };

  async handleCreateClassroom(values: FormValues) {
    const { classroomName, grade, readAloudLevelScheme, language } = values;

    if (!this.props.userProfile) {
      throw new Error('userProfile is null when trying to create a classroom');
    }

    const newClassroomObject: CreateNewClassroomType = {
      className: classroomName,
      grade: parseInt(grade, 10),
      readingLevel: getRecommendedReadingLevelFromGrade(parseInt(grade, 10), language),
      readAloudLevelScheme: readAloudLevelScheme,
      language: language,
      email: this.props.userProfile.email,
      district: this.props.userProfile?.district,
      schoolId: this.props.userProfile?.schoolId,
      account: this.props.userProfile?.account,
      teachersUserId: this.props.userProfile.userId,
    };

    this.setState({ isShowSpinner: true });
    try {
      const newClassroomId = await createClassroomBackend(newClassroomObject);
      const result = await getClassroom(newClassroomId);
      this.props.createClassroom(result);
      this.setState({ isShowSpinner: false });
      this.props.updateClassroom(result);
      this.setState({
        isAnimateOnCreation: true,
        classroomToAnimate: newClassroomObject.className,
      });
    } catch (_) {
      this.setState({ isShowSpinner: false });
      console.error('Classroom::handleCreateClassroom - postServiceRequest failed');
    }
    this.closeClassroomDetailsModal();
  }

  handleOnSettings(classroom: ClassroomType, classroomIndex: number, event: React.MouseEvent) {
    event.preventDefault();

    const editableClassroom = { ...classroom };

    this.setState({
      isShowDetailsModal: true,
      isEditingClassroom: true,
      classroomUnderEditIndex: classroomIndex,
      classroomUnderEdit: editableClassroom,
    });
  }

  async handleSaveChanges(values: FormValues) {
    const { classroomName, grade, readAloudLevelScheme, language } = values;

    // Debug logging for TAKWEEN language issue
    const isTakeweenUser = this.props.userProfile?.licenseLevel === 'TAKWEEN';
    if (isTakeweenUser) {
      console.log('TAKWEEN Debug - Form values received:', values);
      console.log('TAKWEEN Debug - Language from form:', language);
    }

    if (this.state.classroomUnderEditIndex === undefined) {
      throw new Error('ClassroomUnderEditIndex is undefined');
    }
    if (this.state.classroomUnderEdit === undefined) {
      throw new Error('ClassroomUnderEdit is undefined');
    }
    const storedClassroom = this.props.classrooms.list[this.state.classroomUnderEditIndex];
    const classroomUnderEdit = this.state.classroomUnderEdit;
    classroomUnderEdit.className = classroomName;
    classroomUnderEdit.grade = parseInt(grade, 10);

    // For TAKWEEN users, allow Arabic language for all grades
    // For other users, restrict non-English languages to elementary grades only
    classroomUnderEdit.language = parseInt(grade, 10) < 6 || isTakeweenUser ? language : 'en';

    if (isTakeweenUser) {
      console.log('TAKWEEN Debug - Grade:', parseInt(grade, 10));
      console.log('TAKWEEN Debug - Language assigned to classroom:', classroomUnderEdit.language);
    }
    classroomUnderEdit.readingLevel = getRecommendedReadingLevelFromGrade(
      classroomUnderEdit.grade,
      classroomUnderEdit.language || language,
    );
    classroomUnderEdit.readAloudLevelScheme = readAloudLevelScheme;

    const gradeLevelChanged = storedClassroom.grade !== classroomUnderEdit.grade;
    const classroomToUpdate = (() => {
      if (gradeLevelChanged && classroomUnderEdit.completionStatus) {
        const modifiedClassroom = { ...classroomUnderEdit };
        delete modifiedClassroom.completionStatus;

        return modifiedClassroom;
      }

      return classroomUnderEdit;
    })();

    // Debug logging for TAKWEEN language issue
    if (isTakeweenUser) {
      console.log('TAKWEEN Debug - Saving classroom with language:', classroomToUpdate.language);
      console.log('TAKWEEN Debug - Full classroom object:', classroomToUpdate);
    }

    try {
      const updatedClassroom = await updateClassroomBackend(
        this.props.userProfile!.email,
        classroomToUpdate.className,
        classroomToUpdate,
        true,
      );

      // Debug logging for TAKWEEN language issue
      if (isTakeweenUser) {
        console.log('TAKWEEN Debug - Received from backend with language:', updatedClassroom.language);
        console.log('TAKWEEN Debug - Full response:', updatedClassroom);
      }

      if (storedClassroom.className !== classroomUnderEdit.className) {
        this.props.replaceClassroom({ original: storedClassroom, replacement: updatedClassroom });
      } else {
        this.props.updateClassroom(updatedClassroom);
      }
      this.setState({ isShowSpinner: false });
    } catch (error) {
      this.setState({ isShowSpinner: false });
      console.error('Student::handleSaveChanges - updateClassroom update failed', error);
    }

    this.closeClassroomDetailsModal();
  }

  async handleDeleteClassroomNoStudent(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    if (this.state.classroomUnderEditIndex === undefined) {
      throw new Error('ClassroomUnderEditIndex is undefined');
    }
    const classroomToDelete = this.props.classrooms.list[this.state.classroomUnderEditIndex];

    const activeClasses = this.props.classrooms.list.filter((classroom) => !classroom.isArchived);
    // if there is only one active class, then we don't want to delete it
    if (!(activeClasses.length > 1) && !classroomToDelete.isArchived) {
      return;
    }

    if (classroomToDelete.listOfStudentProxies && classroomToDelete.listOfStudentProxies.length > 0) {
      this.setState({
        isShowDetailsModal: false,
        isShowDeleteModal: true,
        classroomUnderDelete: classroomToDelete.className,
        classroomUnderDeleteNumOfStudents: classroomToDelete.listOfStudentProxies.length,
      });
      return;
    }

    this.setState({ isShowSpinner: true });
    try {
      await removeClassroom(classroomToDelete.classroomId);
      this.props.deleteClassroom(classroomToDelete);
      this.setState({ isShowSpinner: false });
    } catch (error: any) {
      this.setState({ isShowSpinner: false });
      console.error('Student::handleDeleteClassroomNoStudent - postServiceRequest delete failed: ', error);
    }

    this.closeClassroomDetailsModal();
  }

  async deleteStudent(classroomToDelete: ClassroomType): Promise<ClassroomType | undefined> {
    const studentsToRemove = classroomToDelete.listOfStudentProxies;
    let updatedClassroom;

    for (const student of studentsToRemove) {
      if (student.userId) {
        // if false then User doesn't have account yet
        try {
          updatedClassroom = await removeStudentAccount(
            student.userId,
            this.props.userProfile!.userId,
            classroomToDelete.classroomId,
          );
          this.props.updateClassroom(updatedClassroom);
        } catch (error: any) {
          console.error('Student::DeleteStudent - postServiceRequest failed', error);
          throw new error();
        }
      }
    }

    return updatedClassroom;
  }

  async handleDeleteClassroomWithStudent() {
    if (this.state.classroomUnderEditIndex === undefined) {
      throw new Error('ClassroomUnderEditIndex is undefined');
    }

    const classroomToDelete = this.props.classrooms.list[this.state.classroomUnderEditIndex];

    this.setState({ isShowSpinner: true });
    const classroom = await this.deleteStudent(classroomToDelete);

    try {
      await removeClassroom((classroom || classroomToDelete).classroomId);
      this.props.deleteClassroom(classroom as ClassroomType);
      this.setState({ isShowSpinner: false });
    } catch (error: any) {
      this.setState({ isShowSpinner: false });
      console.error('Student::handleDeleteClassroomWithStudent - postServiceRequest delete failed', error);
    }
  }

  handleOpenArchiveClassroomModal(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    const activeClasses = this.props.classrooms.list.filter((classroom) => !classroom.isArchived);
    // if there is only one active class, then we don't want to archive it
    if (!(activeClasses.length > 1)) {
      return;
    }
    if (this.state.classroomUnderEditIndex === undefined) {
      throw new Error('ClassroomUnderEditIndex is undefined');
    }
    this.setState({
      isShowDetailsModal: false,
      isShowArchiveModal: true,
    });
  }

  async handleToggleArchive(classroom: ClassroomType) {
    this.setState({ isShowSpinner: true });
    try {
      await updateClassroomBackend(this.props.userProfile.email, classroom.className, {
        isArchived: classroom.isArchived ? false : true,
      });
      const classroomUnderEdit = cloneDeep(classroom);
      this.props.updateClassroom({
        ...classroomUnderEdit,
        isArchived: classroom.isArchived ? false : true,
      });
    } catch (error) {
      logger.logException(error as Error);
    } finally {
      this.setState({
        isShowSpinner: false,
      });
    }
  }

  turnOffCreationAnimation() {
    this.setState({
      isAnimateOnCreation: false,
    });

    window.removeEventListener('animationend', this.turnOffCreationAnimation);
  }

  componentDidMount() {
    initializeGoogleApiClients();
  }

  render() {
    if (!this.props.isAuthenticated || this.props.classrooms.state !== ClassroomsState.loaded) {
      return <SpinnerOverlay isShow={true} />;
    }

    if (this.isLocked()) {
      if (
        this.props.classroom === undefined && !this.props.isUnauthenticated
          ? null
          : this.props.classroom.grade > 5 && this.props.classroom.grade < 9
      ) {
        return (
          <MarketingClassroom marketingCopy='As a Middle School BASIC user, you have full access to our first lesson of each unit. With a free account, you do not need to adjust classroom settings, as users only get access to one grade level of content.' />
        );
      } else if (this.props.classroom.grade > 8) {
        return (
          <MarketingClassroom marketingCopy='As a High School BASIC user, you have full access to our first lesson of each unit. With a free account, you do not need to adjust classroom settings, as users only get access to one grade level of content.' />
        );
      } else {
        return (
          <MarketingClassroom marketingCopy='As a free user, you have access to one level of our storybooks from all our core SEL units. With a free account, you do not need to adjust classroom settings as free users only have access to one level of content.' />
        );
      }
    }
    if (this.props.userProfile === null) {
      throw new Error('userProfile is null');
    }
    const { list, selectedClassroom } = this.props.classrooms;
    const newList = [...list];
    newList.unshift(newList.splice(selectedClassroom, 1)[0]);

    return (
      <div className={`${styles.Classroom} ${utilityStyles.clearfix}`}>
        <Segment className={cx(utilityStyles.line, styles.line)}>
          <div className={styles.titleLine}>
            <div className={styles.title}>
              <p className={segmentStyles.moduleTitle}>Classroom Settings</p>
            </div>
            <PrimaryButton
              small
              className={styles.addNewGroup}
              onClick={(event: React.MouseEvent) => {
                this.handleNewClassroom(event);
              }}
            >
              + New Group
            </PrimaryButton>
          </div>
        </Segment>
        <div className='spacer' />
        <div className='spacer' />
        <ClassroomDetailsModal
          className='ModalDialog'
          isEditingClassroom={this.state.isEditingClassroom}
          classroom={this.state.classroomUnderEdit}
          show={this.state.isShowDetailsModal}
          onHide={this.closeClassroomDetailsModal}
          onCreateClassroom={this.handleCreateClassroom}
          onSaveChanges={this.handleSaveChanges}
          onDelete={this.handleDeleteClassroomNoStudent}
          onArchive={this.handleOpenArchiveClassroomModal}
          autoFocus='true'
          keyboard='true'
          isClassroomNameValid={this.isClassroomNameValid}
          licenseLevel={this.props.userProfile.licenseLevel}
          isTakweenUser={this.props.userProfile?.licenseLevel === 'TAKWEEN'}
        />
        <ImportClassroomModal
          show={this.state.isShowImportModal}
          onHide={this.closeImportModal}
          title='Sync classroom from...'
          onDone={this.onImportDoneCallback}
          userProfile={this.props.userProfile}
          importSource={this.state.importSource}
        />
        <DeleteClassroomModal
          show={this.state.isShowDeleteModal}
          hideModal={() =>
            this.setState({
              isShowDeleteModal: false,
            })
          }
          confirmTestString={this.state.classroomUnderDelete}
          classroomName={this.state.classroomUnderDelete}
          numOfStudents={this.state.classroomUnderDeleteNumOfStudents}
          deleteClassroom={this.handleDeleteClassroomWithStudent}
        />
        <ArchiveClassroomModal
          show={this.state.isShowArchiveModal}
          hideModal={() =>
            this.setState({
              isShowArchiveModal: false,
            })
          }
          confirmTestString={this.state.classroomUnderEdit?.className}
          classroomName={this.state.classroomUnderEdit?.className}
          archiveClassroom={() => this.handleToggleArchive(this.state.classroomUnderEdit as ClassroomType)}
          numOfStudents={this.state.classroomUnderEdit?.listOfStudentProxies?.length || 0}
        />
        <Segment className={utilityStyles.line}>
          <div className={styles.mainSegment}>
            <div className={styles.classroomsSection}>
              {newList.every((classroom) => classroom.isArchived) ? null : <h3>Active Classes</h3>}
              <div className={styles.activeClassroom}>
                {newList &&
                  newList.map((classroom) => {
                    if (this.state.isAnimateOnCreation && this.state.classroomToAnimate === classroom.className) {
                      window.addEventListener('animationend', this.turnOffCreationAnimation);
                      if (classroom.isArchived) {
                        return null;
                      } else {
                        return (
                          <ClassroomCard
                            key={classroom.className}
                            data={classroom}
                            onSettings={this.handleOnSettings}
                            animate
                            isSelectedClassroom={
                              classroom.className ===
                              this.props.classrooms.list[this.props.classrooms.selectedClassroom].className
                            }
                          />
                        );
                      }
                    }
                    if (classroom.isArchived) {
                      return null;
                    } else {
                      return (
                        <ClassroomCard
                          key={classroom.className}
                          data={classroom}
                          onSettings={this.handleOnSettings}
                          isSelectedClassroom={
                            classroom.className ===
                            this.props.classrooms.list[this.props.classrooms.selectedClassroom].className
                          }
                        />
                      );
                    }
                  })}
              </div>
              {newList.every((classroom) => !classroom.isArchived) ? null : <h3>Archived Classes</h3>}
              <div className={styles.archivedClassroom}>
                {newList &&
                  newList.map((classroom) => {
                    if (!classroom.isArchived) return null;
                    return (
                      <ClassroomCard
                        key={classroom.className}
                        data={classroom}
                        onSettings={this.handleOnSettings}
                        isSelectedClassroom={
                          classroom.className ===
                          this.props.classrooms.list[this.props.classrooms.selectedClassroom].className
                        }
                        onUnarchive={this.handleToggleArchive}
                      />
                    );
                  })}
              </div>
            </div>
            <div className={styles.buttonsSection}>
              <div className={styles.buttonsContainer}>
                <SecondaryButton
                  small
                  onClick={() => {
                    this.handleImportNewClassroom();
                  }}
                  className={styles.buttonWithSpacer}
                >
                  + <img alt='Google Classroom logo' src={googleClassroomLogo} />
                  <span className={styles.googleClassroomLogo} />
                </SecondaryButton>
                <SecondaryButton
                  small
                  onClick={() => {
                    this.handleImportNewCleverClassroom();
                  }}
                  className={styles.buttonWithSpacer}
                >
                  + <img alt='Clever Classroom logo' src={cleverClassroomLogo} />
                  <span className={styles.cleverClassroomLogo} />
                </SecondaryButton>
                <SecondaryButton
                  small
                  onClick={() => {
                    this.handleImportNewClassLinkClassroom();
                  }}
                  className={styles.buttonWithSpacer}
                >
                  + <img alt='ClassLink Classroom logo' src={classLinkClassroomLogo} />
                  <span className={styles.classLinkClassroomLogo} />
                </SecondaryButton>
              </div>
              <div className={styles.warning}>
                <ParentalConsentWarning moreStyles={styles.parentalConsentWarning} />
              </div>
            </div>
          </div>
        </Segment>
        <div className='spacer' />
        <div className='spacer' />
        <div className='spacer' />
        <div className='spacer' />
        <SpinnerOverlay isShow={this.state.isShowSpinner || false} />
        <ParentalConsentWarningModal
          isShow={this.state.isShowParentalConsentWarningModal}
          onHide={this.onHideParentalConsentWarningModal}
          onAcknowledge={this.onAcknowledgeParentalConsentWarning}
        />
      </div>
    );
  }
}
interface FormValues {
  classroomName: string;
  grade: string;
  readAloudLevelScheme: string;
  language: string;
}
interface ClassroomDetailsModalProps {
  autoFocus: string;
  className: string;
  classroom: ClassroomType | undefined;
  isClassroomNameValid: (nameToCompare: string) => boolean;
  isEditingClassroom: boolean;
  keyboard: string;
  licenseLevel: string;
  onCreateClassroom: (values: FormValues) => Promise<void>;
  onDelete: (event: React.MouseEvent<HTMLButtonElement>) => Promise<void>;
  onArchive: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onHide: () => void;
  onSaveChanges: (values: FormValues) => Promise<ClassroomType>;
  show: boolean;
  isTakweenUser: boolean;
}
interface ClassroomDetailsModalState {
  classroomName: string | undefined;
  isClassroomNameValid: boolean | undefined;
  isMiddleSchoolClassroom: boolean | undefined;
}
class ClassroomDetailsModal extends Component<ClassroomDetailsModalProps, ClassroomDetailsModalState> {
  gradeOptions: { key: string; value: string }[];
  readAloudLevelSchemeOptions: { key: string; value: string }[];
  storyLanguageOptions: { key: string; value: string }[];
  initialValues: FormValues;
  validationSchema: Yup.AnyObjectSchema;
  constructor(props: ClassroomDetailsModalProps) {
    super(props);

    this.state = {
      classroomName: undefined,
      isClassroomNameValid: undefined,
      isMiddleSchoolClassroom: undefined,
    };

    this.gradeOptions = [
      { key: 'Select Grade', value: 'placeholder' },
      { key: 'Pre-Kindergarten', value: '-1' },
      { key: 'Kindergarten', value: '0' },
      { key: 'First Grade', value: '1' },
      { key: 'Second Grade', value: '2' },
      { key: 'Third Grade', value: '3' },
      { key: 'Fourth Grade', value: '4' },
      { key: 'Fifth Grade', value: '5' },
      { key: 'Sixth Grade', value: '6' },
      { key: 'Seventh Grade', value: '7' },
      { key: 'Eighth Grade', value: '8' },
      { key: 'Ninth Grade', value: '9' },
      { key: 'Tenth Grade', value: '10' },
      { key: 'Eleventh Grade', value: '11' },
      { key: 'Twelfth Grade', value: '12' },
    ];

    this.readAloudLevelSchemeOptions = [
      { key: 'Not Applicable for Secondary Classroom', value: 'disabled' },
      { key: 'Fountas and Pinnell', value: 'fountasAndPinnell' },
      { key: 'Reading Recovery', value: 'readingRecovery' },
      {
        key: 'Developmental Reading Assessment (DRA)',
        value: 'dra',
      },
      { key: 'PM Benchmark', value: 'pmBenchmark' },
    ];

    this.storyLanguageOptions = [
      { key: 'Not Applicable for Secondary Classroom', value: 'disabled' },
      { key: 'English', value: 'en' },
    ];

    const enabledContentTags = getUsersEnableContentTags();
    const isMena = enabledContentTags && enabledContentTags.includes('mena');

    if (isMena) {
      this.storyLanguageOptions.push({ key: 'English and Arabic', value: 'ar' });
    } else {
      this.storyLanguageOptions.push({ key: 'English and Spanish', value: 'es' });
    }

    this.initialValues = {
      classroomName: '',
      grade: '',
      readAloudLevelScheme: 'fountasAndPinnell',
      language: 'en',
    };

    this.validationSchema = Yup.object({
      classroomName: Yup.string().required("Group name is required, must be unique, and cannot be 'default'"),
      grade: Yup.string().required('Please select grade'),
    });

    this.validateClassroomName = this.validateClassroomName.bind(this);
    this.validateClassroomType = this.validateClassroomType.bind(this);
    this.handleOnHide = this.handleOnHide.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
  }

  validateClassroomName(value: string) {
    let error;

    if (!this.props.isClassroomNameValid(value)) {
      error = 'Group name already exists';
    }

    return error;
  }

  validateClassroomType(value: FormValues['grade']) {
    if (parseInt(value, 10) >= 6) {
      this.setState({
        isMiddleSchoolClassroom: true,
      });
    } else {
      this.setState({
        isMiddleSchoolClassroom: false,
      });
    }
  }

  handleOnHide() {
    this.setState({
      classroomName: undefined,
    });
    this.props.onHide();
  }

  handleFormSubmit(values: FormValues) {
    const { language, readAloudLevelScheme } = values;
    console.log('[ClassroomDetailsModal] Form submit - original values:', {
      language,
      readAloudLevelScheme,
      grade: values.grade,
    });
    console.log('[ClassroomDetailsModal] isTakeweenUser:', this.props.isTakweenUser);
    console.log('[ClassroomDetailsModal] isMiddleSchoolClassroom:', this.state.isMiddleSchoolClassroom);

    if (this.state.isMiddleSchoolClassroom && !this.props.isTakweenUser) {
      console.log('[ClassroomDetailsModal] Forcing language to en for middle school non-Takween user');
      values.language = 'en';
      values.readAloudLevelScheme = '';
    } else {
      console.log('[ClassroomDetailsModal] Preserving language:', language);
      values.language = language;
      values.readAloudLevelScheme = readAloudLevelScheme;
    }

    console.log('[ClassroomDetailsModal] Final values before submission:', values);

    if (!this.props.isEditingClassroom) {
      this.props.onCreateClassroom(values);
    } else {
      this.props.onSaveChanges(values);
    }
  }

  componentDidUpdate(prevProps: ClassroomDetailsModalProps) {
    if (
      this.props.classroom !== prevProps.classroom ||
      this.props.isEditingClassroom !== prevProps.isEditingClassroom
    ) {
      if (this.props.classroom && this.props.isEditingClassroom) {
        this.setState({
          classroomName: this.props.classroom.className.trim(),
          isMiddleSchoolClassroom: this.props.classroom.grade >= 6 ? true : undefined,
        });
      } else {
        this.setState({
          isMiddleSchoolClassroom: undefined,
        });
      }
    }
  }

  hasAlreadyStartedLessons = () => {
    if (!this.props.classroom) {
      return false;
    }

    if (!this.props.classroom.completionStatus || this.props.classroom.completionStatus.length === 0) {
      return false;
    }

    const keys = Object.keys(this.props.classroom.completionStatus);

    if (
      this.props.classroom.grade > 5 &&
      keys.some((key) => ['welcome', 'identity', 'stress', 'decision', 'belonging', 'empathy'].includes(key))
    )
      return true;

    const flatEntries = Object.entries(this.props.classroom.completionStatus).flat(Infinity);

    if (flatEntries.some((v) => ['InProgress', 'Completed'].includes(v))) return true;

    return false;
  };

  isBasicUser = () => {
    return !isUserHasKey('KEY_CHANGE_GRADE_LEVEL');
  };

  render() {
    const getClassroomDetailsFields = () => {
      const fields = {} as FormValues;
      // Sometimes classroom is defined, but is an empty object
      if (this.props.classroom && this.props.classroom.className) {
        fields.classroomName = this.props.classroom.className.trim();
        fields.grade = this.props.classroom.grade.toString();
        fields.language = this.props.classroom.language || 'en';
        fields.readAloudLevelScheme = this.props.classroom.readAloudLevelScheme || 'fountasAndPinnell';
      }

      return fields;
    };

    const getButtonsForClassroomDetailsModal = () => {
      const buttons = [];
      if (this.props.isEditingClassroom) {
        buttons.push({
          type: 'submit',
          description: 'Save Changes',
        });
        buttons.push({
          type: 'button',
          description: 'Archive Classroom',
          onClick: this.props.onArchive,
        });
        buttons.push({
          type: 'button',
          description: 'Delete Classroom',
          onClick: this.props.onDelete,
        });
      } else {
        buttons.push({
          type: 'submit',
          description: 'Create Group',
        });
      }

      return buttons;
    };

    const fields = getClassroomDetailsFields();
    const buttons = getButtonsForClassroomDetailsModal();

    const title = this.props.isEditingClassroom ? 'Change classroom settings' : 'Create a new group';
    const hasAlreadyStartedLessons = this.hasAlreadyStartedLessons();
    const isBasicUser = this.isBasicUser();

    const gradeHintIcon = () => {
      if (isBasicUser) {
        return (
          <HintIcon
            className={styles.hintIconStyle}
            hintText={noWhiteSpace`BASIC account users can only access one grade level of lesson matterials. Please use
            the Upgrade button at the top to find out how to upgrade your account.`}
          />
        );
      }

      if (hasAlreadyStartedLessons) {
        return (
          <HintIcon
            className={styles.hintIconStyle}
            hintText={`Grade cannot be changed once you've completed any lesson.
                To change the grade after this has occurred, the lesson progress tracker must be reset`}
          />
        );
      }

      return null;
    };

    const readingLevelHintIcon = () => {
      if (isBasicUser) {
        return (
          <HintIcon
            className={styles.hintIconStyle}
            hintText={noWhiteSpace`BASIC account users can only access one reading level in the storybooks. Please use
            the Upgrade button at the top to find out how to upgrade your account.`}
          />
        );
      }

      return (
        <HintIcon
          className={styles.hintIconStyle}
          hintText={noWhiteSpace`Read Aloud Level sets the sophistication of the
            language used in the storybook, ranging from simple sentences,
            to the use of dialogue, to full-fledged chapters. This allows you
            to pick the reading level according to the reading level designations
            you are accustomed to using`}
        />
      );
    };

    return (
      <Dialog
        open={this.props.show}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            this.handleOnHide();
          }
        }}
      >
        <DialogContent
          className={`${styles.classroomDetails} ModalDialog w-[min(92vw,680px)] rounded-[0.8rem] border-0 bg-white p-0 text-[1.6rem] shadow-[0_14px_34px_rgba(0,0,0,0.2)]`}
          overlayClassName='bg-black/50'
        >
          <div className='relative border-b border-slate-200 px-[2rem] pb-[1.2rem] pt-[1.6rem]'>
            <button
              type='button'
              aria-label='Close'
              onClick={this.handleOnHide}
              className='absolute right-[1.2rem] top-[1.2rem] flex h-[2.6rem] w-[2.6rem] items-center justify-center rounded-full border-0 bg-transparent p-0 text-[2.2rem] leading-none text-slate-600 outline-none transition-colors hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus-visible:ring-1 focus-visible:ring-slate-300'
            >
              ×
            </button>
            <p className="m-0 pr-[3.2rem] font-['GT-Walsheim-Pro-Bold'] text-[2.8rem] leading-[1.15] text-[#242424]">
              {title}
            </p>
          </div>

          <div className='px-[2rem] pb-[2rem] pt-[1.4rem]'>
            <p className='ModalDialogText'>We&apos;ll match your group to the appropriate lesson materials</p>
            <VerticalSpacer ems={1} />
            <Formik
              enableReinitialize
              initialValues={fields.classroomName ? fields : this.initialValues}
              validationSchema={this.validationSchema}
              onSubmit={this.handleFormSubmit}
            >
              {(formik) => {
                return (
                  <Form>
                    <FormikControl
                      className={styles.inputContainer}
                      disabled={hasAlreadyStartedLessons || isBasicUser}
                      control='input'
                      type='text'
                      label='Group Name'
                      name='classroomName'
                      placeholder='e.g. Homeroom'
                      validate={this.validateClassroomName}
                      onBlur={(e: React.FocusEvent) => e.preventDefault()}
                    />
                    <FormikControl
                      className={styles.inputContainer}
                      disabled={hasAlreadyStartedLessons || isBasicUser}
                      control='select'
                      label='Grade'
                      name='grade'
                      options={this.gradeOptions}
                      icon={gradeHintIcon()}
                      validate={this.validateClassroomType}
                      onBlur={(e: React.FocusEvent) => e.preventDefault()}
                    />
                    <FormikControl
                      className={styles.inputContainer}
                      disabled={this.state.isMiddleSchoolClassroom || isBasicUser}
                      control='select'
                      label='Read Aloud Level Scheme'
                      name='readAloudLevelScheme'
                      options={this.readAloudLevelSchemeOptions}
                      icon={readingLevelHintIcon()}
                      onBlur={(e: React.FocusEvent) => e.preventDefault()}
                      novalue={this.state.isMiddleSchoolClassroom}
                    />
                    <FormikControl
                      disabled={this.state.isMiddleSchoolClassroom && !this.props.isTakweenUser}
                      className={styles.inputContainer}
                      disalb
                      control='select'
                      label='Resource Language'
                      name='language'
                      options={this.storyLanguageOptions}
                      onBlur={(e: React.FocusEvent) => e.preventDefault()}
                      novalue={this.state.isMiddleSchoolClassroom && !this.props.isTakweenUser}
                    />
                    <div className={styles.ModalDialogFooter}>
                      <div className={styles.actionButtonsGroup}>
                        {buttons.map((button, index) => (
                          <DetailsButton
                            key={index}
                            type={button.type}
                            description={button.description}
                            onClick={button.onClick}
                            isArchivedClassroom={this.props.classroom?.isArchived}
                          />
                        ))}
                        <TextButton bold inline grey uppercase underlineHover onClick={this.props.onHide}>
                          or cancel
                        </TextButton>
                      </div>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  classrooms: {
    state: state.classrooms.state,
    list: state.classrooms.classrooms,
    selectedClassroom: state.classrooms.selectedClassroom,
  },
  classroom: selectClassroom(state),
  userProfile: state.user.userProfile,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  updateClassroom: (classroom: ClassroomType) => dispatch(updateClassroom(classroom)),
  replaceClassroom: ({ original, replacement }: { original: ClassroomType; replacement: ClassroomType }) =>
    dispatch(replaceClassroom({ original, replacement })),
  deleteClassroom: (classroom: ClassroomType) => dispatch(deleteClassroom(classroom)),
  createClassroom: (classroom: ClassroomType) => dispatch(createClassroom(classroom)),
  updateUserProfile: ({ field, value }: FieldUpdatePair) => dispatch(updateField({ field, value })),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ClassroomPropsFromRedux = ConnectedProps<typeof connector>;

function ClassFunctionWrapper(props: ClassroomProps) {
  const onShowErrorMessage = useErrorStatus();
  const history = useHistory();
  const LockingClassroom = lockWithPreviewUnlessHasAny('KEY_MANAGE_CLASSROOMS')(Classroom);
  return <LockingClassroom {...props} history={history} onShowErrorMessage={onShowErrorMessage} />;
}

export default connector(ClassFunctionWrapper);
